import { useDispatch, useSelector } from "react-redux";

import {
  useCancelAnalysisJob,
  useRerunAnalysisJob
} from "@/api/analysis/useAnalysisAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import CollapseView from "@/components/ui/collapseView";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { RootState } from "@/store";
import { removeQueuedJobId } from "@/store/slice/analysisJob";
import { setDatasetHistory } from "@/store/slice/datasetSlice";
import {
  clearCurrRunningAnalysisId,
  setCurrRunningAnalysisId,
  setLatestAnalysisId
} from "@/store/slice/spatialSlice";
import { toggleAnalysisJobList } from "@/store/slice/uiSlice";
import { AnalysisJobItem, AnalysisJobStatus } from "@/types/analysis";
import { GetUserProfileData } from "@/types/userProfile";

import JobItem from "./jobItem";

export interface JobSection {
  title: string;
  jobs: AnalysisJobItem[];
  unOpenedJobIds?: string[];
  expanded?: boolean;
  userProfileData?: GetUserProfileData;
  onOpen?: (id: string) => void;
  onCancel?: (id: string) => void;
  onRerun?: (id: string) => void;
}

export const TEST_ID_PREFIX = "analysis-job-list";

const JobListSection = ({
  title,
  jobs,
  unOpenedJobIds,
  expanded = false,
  onOpen,
  onCancel,
  onRerun,
  userProfileData
}: JobSection) => (
  <CollapseView
    expanded={expanded}
    title={`${title} (${jobs.length || 0})`}
    className="p-0 font-semibold text-sm"
    titleClassName="text-sm"
  >
    <div className="flex flex-col gap-2">
      {jobs.map((job) => (
        <JobItem
          key={job.id}
          data={job}
          unOpenedJobIds={unOpenedJobIds}
          onOpen={onOpen}
          onCancel={onCancel}
          onRerun={onRerun}
          userProfileData={userProfileData}
        />
      ))}
    </div>
  </CollapseView>
);

export default function AnalysisJobList() {
  const dispatch = useDispatch();
  const { showError, showLoading, showWarning, clearLoadingState } =
    useLoadingAndToast();

  const { analysisJobs, unOpenedJobIds } = useSelector(
    (state: RootState) => state.analysisJob
  );
  const { currRunningAnalysisId } = useSelector(
    (state: RootState) => state.spatial
  );
  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );

  const { data: userProfileData } = useGetUserProfileAPI();

  const runningList = analysisJobs.filter(
    (job) => job.status === AnalysisJobStatus.IN_PROGRESS
  );
  const waitingList = analysisJobs.filter(
    (job) => job.status === AnalysisJobStatus.QUEUED
  );
  const completedList = analysisJobs.filter(
    (job) => job.status === AnalysisJobStatus.SUCCESS
  );

  const sections: JobSection[] = [
    { title: "Running", jobs: runningList },
    { title: "Waiting", jobs: waitingList },
    { title: "Completed", jobs: completedList }
  ];

  const handleErrorToast = () =>
    showError(
      <NotificationContent
        title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
        description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
      />
    );

  const { mutate: cancelAnalysisJob } = useCancelAnalysisJob({
    onError: handleErrorToast,
    onSuccess: (_, { analysisId }) => {
      clearLoadingState();
      if (analysisId === currRunningAnalysisId) {
        dispatch(clearCurrRunningAnalysisId());
      }
    }
  });

  const { mutate: rerunAnalysisJob } = useRerunAnalysisJob({
    onError: handleErrorToast,
    onMutate: () => {
      showLoading(
        <NotificationContent
          title={MESSAGE.LOADING_SPATIAL_ANALYSIS}
          description={MESSAGE.PREPARE_TABULAR_DATA_AND_MAPVIEW}
        />
      );
    },
    onSuccess: (data) => {
      clearLoadingState();
      if (data?.message) {
        showWarning(
          <NotificationContent
            title="Queue Limit Reached"
            description={data.message}
          />
        );
      } else {
        // go to src/pages/home/index.tsx:71 to fetch analysis status
        dispatch(setCurrRunningAnalysisId(data.id));
        dispatch(setLatestAnalysisId(data.id));
        dispatch(
          setDatasetHistory({
            datasets: datasets,
            visualizationOrder: visualizationOrder,
            rawDatasets: rawDatasets
          })
        );
      }
    }
  });

  return (
    <div className="absolute top-[30px] right-20 z-[9] border-gainsboro bg-white rounded-lg w-[368px] max-h-[calc(100vh-54px)] overflow-hidden p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-txtHeading text-lg font-semibold">Analysis List</h3>
        <button
          className="text-sm"
          onClick={() => dispatch(toggleAnalysisJobList())}
        >
          <CloseIcon
            data-testid={`${TEST_ID_PREFIX}-close-icon`}
            className="inline-block"
          />
        </button>
      </div>

      <div className="overflow-y-auto custom-scrollbar max-h-[87vh] pr-2 pb-2">
        {sections.map((section) => (
          <JobListSection
            key={section.title}
            {...section}
            unOpenedJobIds={unOpenedJobIds}
            expanded
            onOpen={(analysisId) => {
              // removeUnOpenedJobId handled on useAnalysisResult when stream:done
              dispatch(setCurrRunningAnalysisId(analysisId));
              dispatch(setLatestAnalysisId(analysisId));
            }}
            userProfileData={userProfileData}
            onCancel={(analysisId) => {
              dispatch(removeQueuedJobId(analysisId));
              cancelAnalysisJob({ analysisId });
            }}
            onRerun={(analysisId) => {
              rerunAnalysisJob({ analysisId });
            }}
          />
        ))}
      </div>
    </div>
  );
}
